import React from 'react';
import { Alert, Box, Chip, Divider, List, ListItem, Typography } from '@mui/material';
import { Email, Info } from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { LoadingSpinner } from './LoadingSpinner';

export interface ChangeEmailDeliveryLogItem {
  id: number;
  recipient_email: string | null;
  status: string;
  sent_at: string;
  sent_by?: string;
  trigger?: string | null;
  agency_name?: string | null;
}

interface ChangeEmailDeliveryLogListProps {
  logs: ChangeEmailDeliveryLogItem[];
  loading?: boolean;
}

const AUTOMATIC_SENDER = 'GreeningAI';
const MANUAL_TRIGGER = 'manual';

function formatSentAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
}

function statusLabel(status: string): string {
  if (status === 'sent_simulated') return 'Simulado';
  if (status === 'sent') return 'Enviado';
  return status;
}

function resolveLogSentBy(log: ChangeEmailDeliveryLogItem): string {
  if (log.sent_by?.trim()) return log.sent_by.trim();
  if (log.trigger === MANUAL_TRIGGER) return 'Usuário';
  return AUTOMATIC_SENDER;
}

/** Metadados de envio (destinatário, data/hora, remetente) — sem corpo da mensagem. */
export function ChangeEmailDeliveryLogList({ logs, loading = false }: ChangeEmailDeliveryLogListProps) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <LoadingSpinner message="Carregando logs de envio…" />
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="text.secondary">
          Nenhum e-mail registrado para esta norma monitorada ainda.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
        <Typography variant="body2">
          Total de <strong>{logs.length}</strong> envios registrados
        </Typography>
      </Alert>
      <List sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        {logs.map((log, index) => {
          const sentBy = resolveLogSentBy(log);
          return (
            <React.Fragment key={log.id}>
              <ListItem sx={{ alignItems: 'flex-start', gap: 2, py: 1.5 }}>
                <Email color="action" sx={{ mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600}>
                      {log.recipient_email || '—'}
                    </Typography>
                    <Chip
                      size="small"
                      label={statusLabel(log.status)}
                      color={log.status.startsWith('sent') ? 'success' : 'default'}
                    />
                    {log.agency_name?.trim() && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={log.agency_name.trim()}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Enviado em <strong>{formatSentAt(log.sent_at)}</strong>
                    {' · '}
                    Por <strong>{sentBy}</strong>
                  </Typography>
                </Box>
              </ListItem>
              {index < logs.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    </>
  );
}
